import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import { getCompanyCalendar } from '~/database/supabase/calendar/queries.server.ts';
import type { CleanCalendar } from '~/database/supabase/calendar/types.ts';
import { getLatestNetDebt } from '~/database/supabase/financials/queries.server.ts';
import type { CleanCompanyBasics } from '~/database/supabase/financials/types.ts';
import { getInsiderTransactionsByTicker } from '~/database/supabase/insider/insider_transactions/queries.server.ts';
import type { CleanInsiderTransaction } from '~/database/supabase/insider/insider_transactions/types.js';
import { getInstrumentAndItsCompanyByTicker } from '~/database/supabase/instrument_company/queries.server.ts';
import { getCompanyNews } from '~/database/supabase/news/queries.server.ts';
import { getOrAddUserNote } from '~/database/supabase/notes/queries.server.ts';
import type { CleanNote } from '~/database/supabase/notes/types.js';
import type { DefaultKeyStatistics } from '~/integrations/yahoo/queries.js';
import {
	fetchQuoteDataCalendarEvents,
	fetchQuoteDataDefaultKeyStatistics,
} from '~/integrations/yahoo/queries.server.ts';
import { getHints } from '~/utils/client-hints.tsx';
import getNewsApi from '~/views/instrument/utils/news-and-research-utils.server.ts';
import type { ConsolidatedNewsFormat } from '~/views/instrument/utils/news-and-research-utils.ts';
import consolidateNewsSources from '~/views/instrument/utils/news-and-research-utils.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

export interface InstrumentOverviewPageData {
	readonly companyCalendar: CleanCalendar[] | null;
	readonly companyNetDebt: CleanCompanyBasics | null | undefined;
	readonly defaultKeyStatistics: DefaultKeyStatistics | null;
	readonly insiderTransactions: CleanInsiderTransaction[] | null;
	readonly marketNews: ConsolidatedNewsFormat[];
	readonly userCompanyNote: CleanNote | null;
	readonly userLocale: string;
}

const mergeCalendarEvents = async (ticker: string, companyId: number | null | undefined) => {
	const companyCalendar = await getCompanyCalendar(companyId);

	if (companyCalendar && companyCalendar.length > 0) {
		return companyCalendar.map((ele) => ({
			...ele,
			company_id: ele.company_id,
			id: ele.id,
		})) as CleanCalendar[];
	}

	const val = await fetchQuoteDataCalendarEvents({ ticker });

	if (!val) {
		return null;
	}

	const alternativeCalendarEvents = val.earnings.earningsDate?.map((date, index) => ({
		company_id: companyId ?? null,
		created_at: null,
		date: date.raw ? new Date(date.raw * 1000) : null,
		description: 'Earnings',
		id: index,
		lang: 'en',
		modified_at: null,
	}));

	return alternativeCalendarEvents as CleanCalendar[];
};

const GetInstrumentOverviewPageDataSchema = z.object({
	ticker: z.string().optional(),
});

export const getInstrumentOverviewPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.instrument.lookup',
				'supabase.calendar.company',
				'supabase.financials.net-debt',
				'supabase.notes.user-company',
				'supabase.insider-transactions',
				'supabase.news.company',
				'fmp.enterprise-value',
				'yahoo.quote-data',
			],
			relatesTo: 'instrument.overview',
		}),
	])
	.inputValidator(GetInstrumentOverviewPageDataSchema)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;
		const ticker = data.ticker;

		let companyCalendar: CleanCalendar[] | null = null;
		let defaultKeyStatistics: DefaultKeyStatistics | null = null;
		let companyNetDebt: CleanCompanyBasics | null | undefined = null;
		let userCompanyNote: CleanNote | null = null;
		let insiderTransactions: CleanInsiderTransaction[] | null = null;
		let marketNews: ConsolidatedNewsFormat[] = [];

		if (ticker) {
			const instrument = await getInstrumentAndItsCompanyByTicker(ticker);

			const values = await Promise.all([
				mergeCalendarEvents(ticker, Number(instrument?.company_id)),
				fetchQuoteDataDefaultKeyStatistics({ ticker }),
				getLatestNetDebt(Number(instrument?.company_id)),
				getOrAddUserNote({
					companyId: Number(instrument?.company_id),
					userId: authSession.userId,
				}),
			]);

			companyCalendar = values[0];
			defaultKeyStatistics = values[1];
			companyNetDebt = values[2];
			userCompanyNote = values[3];

			insiderTransactions = await getInsiderTransactionsByTicker({ ticker });

			if (instrument?.company_id) {
				const [news, newsApi] = await Promise.all([
					instrument.company?.mfn_id ? getCompanyNews(Number(instrument.company_id)) : Promise.resolve(null),
					getNewsApi(instrument),
				]);

				marketNews = consolidateNewsSources({
					mfn: news,
					newsApi,
				});
			}
		}

		return {
			companyCalendar,
			companyNetDebt,
			defaultKeyStatistics,
			insiderTransactions,
			marketNews,
			userCompanyNote,
			userLocale,
		} satisfies InstrumentOverviewPageData;
	});
